import DSBox from "../../../system-design/DSBox";
import DSCircularProgress from "../../../system-design/DSCircularProgress";
import useKlydoReviewsContainer, {
  REVIEW_UPDATED_AT_FIELD,
} from "../hooks/useKlydoReviewsContainer";
import DSTypography from "../../../system-design/DSTypography";
import { Klydo, PublicationReviewStates } from "../../../../api/klydo";
import DSTextField from "../../../system-design/DSTextField";
import KlydoLoopUrl from "../../components/KlydoLoopUrl";
import { DSBaseTable, DSTableCell } from "../../../system-design/Table";
import { Column } from "../../../system-design/Table/DSBaseTable/components/DSBaseTable";
import KlydoReviewModal from "./KlydoReviewModal";
import { useCallback, useState } from "react";
import { useTheme } from "@mui/material";
import DSSwitch from "../../../system-design/DSSwitch";
import { useKlydoRatingStatsBatch } from "../../hooks/useKlydoRatingStats";

const KlydoReviewsContainer = () => {
  const [onlyPending, setOnlyPending] = useState(true);
  const {
    klydos,
    isLoading,
    fetchNextPage,
    handleFreeSearch,
    handleSortClick,
    sortState,
    columns,
  } = useKlydoReviewsContainer();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<Klydo | null>(null);
  const { data: ratingStatsById } = useKlydoRatingStatsBatch(
    (klydos ?? []).map((k) => k.id),
  );
  const onPressRow = useCallback(
    (row: Klydo) => {
      setIsModalOpen(true);
      setSelectedRow(row);
    },
    [setIsModalOpen, selectedRow],
  );
  const { spacing } = useTheme();

  const buildTableRows = (klydo: Klydo, columns: Column<Klydo>[]) => (
    <>
      {columns.map((col) => (
        <DSTableCell
          key={col.field as string}
          style={{
            width: col.size || `${100 / columns?.length}%`,
            whiteSpace: "nowrap",
          }}
        >
          {col.field === "loopUrl" ? (
            <DSBox
              sx={{
                width: spacing(10),
                height: spacing(10),
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <KlydoLoopUrl loopUrl={klydo.klydoAnimation!.xs} />
            </DSBox>
          ) : col.field === "public" ? (
            klydo.public?.reviewStatus?.state || "N/A"
          ) : col.field === "author" ? (
            klydo.author?.name || "N/A"
          ) : (col.field as string) === "avgRating" ? (
            ratingStatsById?.[klydo.id]?.count ? (
              ratingStatsById[klydo.id].average.toFixed(1)
            ) : (
              "-"
            )
          ) : (col.field as string) === "ratingCount" ? (
            (ratingStatsById?.[klydo.id]?.count ?? 0).toString()
          ) : (col.field as string) === REVIEW_UPDATED_AT_FIELD ? (
            klydo.public?.reviewStatus?.updatedAt ? (
              new Date(klydo.public.reviewStatus.updatedAt).toLocaleString(
                "en-GB",
              )
            ) : (
              "N/A"
            )
          ) : (
            String(klydo[col.field])
          )}
        </DSTableCell>
      ))}
    </>
  );

  return (
    <DSBox>
      <DSBox mb={3}>
        <DSTypography variant="h6" mb={1}>
          Free Search
        </DSTypography>
        <DSTextField
          fullWidth
          variant="outlined"
          placeholder="Happy Klydo"
          onChange={(e) => {
            handleFreeSearch(e.target.value);
          }}
        />
        <DSBox mt={1}>
          <DSSwitch
            title="Only Pending"
            checked={onlyPending}
            onChange={(e) => setOnlyPending(e.target.checked)}
          />
          <DSTypography variant="caption">
            Only show pending reviews
          </DSTypography>
        </DSBox>
      </DSBox>
      {klydos ? (
        <DSBaseTable
          data={klydos.filter((klydo) =>
            onlyPending
              ? klydo.public?.reviewStatus?.state ===
                PublicationReviewStates.Pending
              : true,
          )}
          columns={columns}
          rowBuilder={buildTableRows}
          fetchMore={fetchNextPage}
          onPressRow={onPressRow}
          onSort={handleSortClick}
          sortState={sortState}
        />
      ) : (
        !isLoading && (
          <DSTypography variant="body1" textAlign="center">
            <p>No Klydos found in review</p>
          </DSTypography>
        )
      )}
      {isLoading && (
        <DSBox
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <DSCircularProgress />
        </DSBox>
      )}
      {selectedRow && (
        <KlydoReviewModal
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedRow(null);
          }}
          row={selectedRow}
        />
      )}
    </DSBox>
  );
};

export default KlydoReviewsContainer;
